// Main game module — wires the world, players, AI, audio, ripples, UI.
// Two perspectives: HIDER (third-person chase) and SEEKER (first-person FPS).

import * as THREE from 'three';
import { buildWorld } from './world/world.js';
import { Walker } from './player/walker.js';
import { makeCharacter, animateCharacter } from './player/character.js';
import { AISeeker } from './ai/seeker.js';
import { GameAudio } from './audio/audio.js';
import { FootstepRipples } from './render/footstep-ripples.js';
import { StaminaRing, makeStatusBoard } from './render/diegetic.js';
import { Input } from './input.js';

export async function startGame(canvas, hud) {
  // ---------- Renderer / scene ----------
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  // Faceted moonlit lighting: low-angle silver-blue key + indigo ambient.
  const keyLight = new THREE.DirectionalLight(0x9ec8ff, 1.4);
  keyLight.position.set(8, 6, 4);
  scene.add(keyLight);
  const ambient = new THREE.AmbientLight(0x2c2046, 0.55);
  scene.add(ambient);
  const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 1500);

  const resize = () => {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  new ResizeObserver(resize).observe(canvas);
  resize();

  // ---------- World ----------
  const world = await buildWorld(scene, { cycleLights: { keyLight, ambient } });
  const { planet, props, huts, blackHole, aurora, comets, cycle } = world;

  // Mount status boards on the front face of each hut
  const board = makeStatusBoard({ rounds: 3 });
  // Place near the first hut's window
  huts[0].add(board);
  board.position.set(0, 2.4, 1.3);
  board.rotation.x = -0.3;

  // ---------- Players ----------
  const walker = new Walker({ planet, eyeHeight: 1.7 });
  walker.spawnAt(2.0, -0.2);

  const hiderMesh = makeCharacter({ bodyColor: 0x5a4f8a, mossColor: 0x4a3a7a, eyeColor: 0x9ec8ff });
  scene.add(hiderMesh);

  // Diegetic stamina bracelet on the golem's body (orbits the torso)
  const stamina = new StaminaRing({ color: 0x9ec8ff, radius: 0.13, tube: 0.025 });
  hiderMesh.add(stamina.group);
  stamina.group.position.set(0.0, 0.85, 0.55);
  stamina.group.rotation.x = Math.PI / 2;

  const seeker = new AISeeker({ planet, speed: 4.0, chaseSpeed: 6.5 });
  seeker.spawnAt(-1.3, 0.6);
  scene.add(seeker.mesh);

  const ripples = new FootstepRipples(scene, planet);
  const audio = new GameAudio();

  // ---------- Input ----------
  const input = new Input(canvas);

  // ---------- Camera modes ----------
  // HIDER = third-person chase, SEEKER = first-person on the AI seeker (spectator FP).
  // The active player is always the human Walker; perspective just changes the camera.
  let perspective = 'HIDER'; // 'HIDER' or 'SEEKER'
  const camOffset = new THREE.Vector3(0, 2.0, 6.5);

  // ---------- Round state ----------
  const round = {
    phase: 'COUNTDOWN',  // COUNTDOWN -> PLAYING -> RESULT
    timer: 5.0,
    duration: 90.0,
    elapsed: 0,
    won: 0,
    lost: 0,
    seekerSightTime: 0,  // accumulated time the seeker has had eyes on you
    catchTime: 1.5,      // need this many seconds of LOS to be caught
    message: 'Hide!',
  };

  function resetRound() {
    walker.spawnAt(Math.random() * Math.PI * 2, (Math.random() - 0.5) * Math.PI * 0.6);
    seeker.spawnAt(walker.dir.angleTo(new THREE.Vector3(1,0,0)) + Math.PI, 0.0);
    round.phase = 'COUNTDOWN';
    round.timer = 5.0;
    round.elapsed = 0;
    round.seekerSightTime = 0;
    round.message = 'Hide!';
  }

  // ---------- Loop ----------
  let last = performance.now();
  let elapsed = 0;
  function tick(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    elapsed += dt;

    const inp = input.read();

    // Toggle perspective on V (one-shot)
    if (inp.togglePerspective) {
      perspective = (perspective === 'HIDER') ? 'SEEKER' : 'HIDER';
      // debounce
      input.keys.delete('KeyV');
    }

    // Update walker
    walker.update(round.phase === 'PLAYING' ? inp : { forward: 0, right: 0, sprint: false, crouch: false, mouseDX: inp.mouseDX, mouseDY: inp.mouseDY }, dt, props);
    stamina.set(walker.stamina);

    // Position the visible hider mesh at the walker
    hiderMesh.position.copy(walker.dir).multiplyScalar(planet.sampleRadius(walker.dir));
    // Build mesh orientation directly from walker's basis (no recomposition).
    const meshM = new THREE.Matrix4().makeBasis(
      walker.right.clone(),
      walker.up.clone(),
      walker.forward.clone(), // character mesh faces +Z (eyes at z=+0.34)
    );
    hiderMesh.quaternion.setFromRotationMatrix(meshM);
    // Crouch shrink
    hiderMesh.scale.set(1, walker.crouching ? 0.65 : 1, 1);
    animateCharacter(hiderMesh, elapsed, walker.moving ? (walker.sprinting ? 1.3 : walker.crouching ? 0.5 : 1.0) : 0);

    // Footstep ripples / audio
    if (round.phase === 'PLAYING' && walker.shouldEmitFootstep()) {
      const intensity = walker.noiseLevel();
      ripples.spawn(walker.dir.clone(), intensity);
      audio.footstep({ crouched: walker.crouching, sprinting: walker.sprinting });
    }

    // Update AI seeker
    if (round.phase === 'PLAYING') {
      seeker.update(dt, walker, props, elapsed);
    }

    ripples.update(dt);

    // Round bookkeeping
    if (round.phase === 'COUNTDOWN') {
      round.timer -= dt;
      round.message = `Hide! ${Math.ceil(round.timer)}`;
      if (round.timer <= 0) { round.phase = 'PLAYING'; round.message = ''; }
    } else if (round.phase === 'PLAYING') {
      round.elapsed += dt;
      // Track sustained line-of-sight for catch
      const seen = seeker._canSee(walker, props);
      round.seekerSightTime = seen ? round.seekerSightTime + dt : Math.max(0, round.seekerSightTime - dt * 0.8);
      if (round.seekerSightTime >= round.catchTime) {
        round.phase = 'RESULT';
        round.lost++;
        round.message = 'You were spotted!';
        round.timer = 3.0;
        audio.caught();
      } else if (round.elapsed >= round.duration) {
        round.phase = 'RESULT';
        round.won++;
        round.message = 'You survived!';
        round.timer = 3.0;
        board.userData.setProgress(round.won);
      }
    } else if (round.phase === 'RESULT') {
      round.timer -= dt;
      if (round.timer <= 0) resetRound();
    }

    // Sky updates
    aurora.userData.update(elapsed);
    comets.userData.update(elapsed);
    blackHole.userData.update(elapsed);
    cycle.update(elapsed);

    // Camera
    if (perspective === 'HIDER') {
      // Chase cam: behind & above the hider
      const back = walker.forward.clone().multiplyScalar(-camOffset.z);
      const up = walker.up.clone().multiplyScalar(camOffset.y);
      const target = walker.position.clone().add(up.clone().multiplyScalar(0.4));
      camera.position.copy(walker.position).add(back).add(up);
      // Apply pitch by lifting/lowering the target a bit
      target.add(walker.up.clone().multiplyScalar(walker.pitch * 2));
      camera.up.copy(walker.up);
      camera.lookAt(target);
    } else {
      // First-person seeker: through the AI's eyes
      camera.position.copy(seeker.position).add(seeker.up.clone().multiplyScalar(1.6));
      const fwd = seeker.forward.clone();
      camera.up.copy(seeker.up);
      camera.lookAt(camera.position.clone().add(fwd));
    }

    // HUD updates (kept tiny — diegetic-first)
    if (hud) {
      hud.setMessage(round.message);
      hud.setPerspective(perspective);
      hud.setSeekerState(seeker.state);
      hud.setRoundProgress(round.elapsed / round.duration, round.won, round.lost);
      hud.setCaughtMeter(round.seekerSightTime / round.catchTime);
    }

    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  }

  // First user gesture starts audio
  canvas.addEventListener('pointerdown', () => { audio.resume(); audio.startAmbient(); }, { once: true });

  requestAnimationFrame(tick);
  return { resetRound };
}
